import type { ISODate, PlainDateTimeStr, Timing } from './model.ts';
import { Temporal, addDays, daysBetween, resolveLocal } from './time.ts';

const toMinuteStr = (p: Temporal.PlainDateTime) => p.toString({ smallestUnit: 'minute' });

export interface TimingResult {
  timing: Timing;
  /** Start eller slut hamnade i ett sommartidsglapp och flyttades fram – visa för användaren. */
  adjusted: boolean;
}

/**
 * Beräknar nytt slut givet ny start och ursprunglig tidsättning, enligt durationPolicy:
 *  - wall: samma väggklockdifferens (08–16 förblir 08–16, nattpass 22–06 förblir 22–06)
 *  - elapsed: samma faktiska längd (8 h förblir 8 h även över sommartidsomställning)
 */
function endFor(t: Extract<Timing, { kind: 'timed' }>, newStart: Temporal.PlainDateTime): { end: Temporal.PlainDateTime; adjusted: boolean } {
  const oldStart = Temporal.PlainDateTime.from(t.start);
  const oldEnd = Temporal.PlainDateTime.from(t.end);
  if (t.durationPolicy === 'wall') {
    const wallDiff = oldStart.until(oldEnd, { largestUnit: 'hours' });
    const end = newStart.add(wallDiff);
    return { end, adjusted: resolveLocal(toMinuteStr(end), t.timeZone, 'compatible').adjusted };
  }
  const s0 = resolveLocal(t.start, t.timeZone, 'compatible').zdt;
  const e0 = resolveLocal(t.end, t.timeZone, 'compatible').zdt;
  const elapsed = s0.until(e0, { largestUnit: 'hours' });
  const s1 = resolveLocal(toMinuteStr(newStart), t.timeZone, 'compatible').zdt;
  return { end: s1.add(elapsed).toPlainDateTime(), adjusted: false };
}

/** Flytta till nytt datum men behåll avsedd lokal starttid. */
export function moveTimingToDate(t: Timing, newDate: ISODate): TimingResult {
  if (t.kind === 'allDay') {
    const span = Math.max(1, daysBetween(t.start, t.endExclusive));
    return { timing: { kind: 'allDay', start: newDate, endExclusive: addDays(newDate, span) }, adjusted: false };
  }
  const oldStart = Temporal.PlainDateTime.from(t.start);
  const newStart = Temporal.PlainDate.from(newDate).toPlainDateTime(oldStart.toPlainTime());
  return moveTimingToStart(t, toMinuteStr(newStart));
}

export function shiftTimingDays(t: Timing, days: number): TimingResult {
  const start = t.kind === 'allDay' ? t.start : t.start.slice(0, 10);
  return moveTimingToDate(t, addDays(start, days));
}

/** Tidsvy: ny lokal starttid (dra i veckovy). Längden enligt durationPolicy. */
export function moveTimingToStart(t: Timing, newStartLocal: PlainDateTimeStr): TimingResult {
  if (t.kind === 'allDay') return moveTimingToDate(t, newStartLocal.slice(0, 10));
  const ns = Temporal.PlainDateTime.from(newStartLocal);
  const startRes = resolveLocal(toMinuteStr(ns), t.timeZone, 'compatible');
  const { end, adjusted: endAdj } = endFor(t, ns);
  // Tid i glappet: lagra den faktiska (framflyttade) väggtiden så att visning och larm stämmer.
  const startLocal = startRes.zdt.toPlainDateTime();
  return {
    timing: { ...t, start: toMinuteStr(startLocal), end: toMinuteStr(Temporal.PlainDateTime.compare(end, startLocal) < 0 ? startLocal : end) },
    adjusted: startRes.adjusted || endAdj,
  };
}

/** Ändra längd (dra i nederkant). */
export function resizeTiming(t: Timing, newEnd: PlainDateTimeStr | ISODate): TimingResult {
  if (t.kind === 'allDay') {
    const d = newEnd.slice(0, 10);
    return { timing: { ...t, endExclusive: daysBetween(t.start, d) >= 1 ? d : addDays(t.start, 1) }, adjusted: false };
  }
  const end = Temporal.PlainDateTime.from(newEnd);
  const start = Temporal.PlainDateTime.from(t.start);
  const safeEnd = Temporal.PlainDateTime.compare(end, start) <= 0 ? start.add({ minutes: 15 }) : end;
  const r = resolveLocal(toMinuteStr(safeEnd), t.timeZone, 'compatible');
  return { timing: { ...t, end: toMinuteStr(r.zdt.toPlainDateTime()) }, adjusted: r.adjusted };
}

export function durationMinutes(t: Timing): number {
  if (t.kind === 'allDay') return daysBetween(t.start, t.endExclusive) * 1440;
  const s = resolveLocal(t.start, t.timeZone, 'compatible').zdt;
  const e = resolveLocal(t.end, t.timeZone, 'compatible').zdt;
  return Math.round(s.until(e, { largestUnit: 'hours' }).total({ unit: 'minutes' }));
}

/** Startar och slutar på olika kalenderdagar i sin egen zon (t.ex. nattpass). */
export function crossesMidnight(t: Timing): boolean {
  if (t.kind === 'allDay') return daysBetween(t.start, t.endExclusive) > 1;
  return t.start.slice(0, 10) !== t.end.slice(0, 10) && t.end.slice(11) !== '00:00';
}
